import {toast} from "react-toastify";
import {useSelector} from "react-redux";
import {Alert, Button, Modal, Spinner, Table} from "react-bootstrap";
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import IconButton from "../../../common/IconButton";
import EventBus from "../../../../app/eventbus/EventBus";
import billingService from "../../../../services/billingService";
import {getPackageCodeLabel} from "../../../../enum/BillingPackageCode";
import {getBillingErrorMessage} from "../../../../helpers/billing-response";
import {formatBillingDate, formatBillingMoney, isBillingSoftDeleted} from "../../../../helpers/billing-format";
import AddOrganizationPackageDiscountModal from "../modal/AddOrganizationPackageDiscountModal";
import UpdateOrganizationPackageDiscountModal from "../modal/UpdateOrganizationPackageDiscountModal";
import {selectActiveOrganization} from "../../../../app/store/reducers/auth/authReducer";
import {selectLang, selectSystemMoneyFormat, Translate} from "../../../../app/store/reducers/main/mainReducer";
import {
    ADD_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED,
    DELETE_ORGANIZATION_PACKAGE_DISCOUNT_FAILED,
    DELETE_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED,
    UPDATE_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED
} from "../../../../app/eventbus/billingEvents";

/**
 * Tashkilotning paket chegirmalari (AdminOrganizationPackageDiscount).
 *
 * Chegirma — qat'iy summa (foiz emas), paket narxidan oshmaydi va tashkilot shu paketni
 * sotib olganda qo'llanadi. Bitta tashkilot + paket uchun bitta faol chegirma bo'ladi,
 * shuning uchun qo'shish formasida chegirmasi bor paketlar ro'yxatdan chiqariladi.
 */
const OrganizationPackageDiscounts = ({organizationId}) => {
    const [discounts, setDiscounts] = useState([]);
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [packagesError, setPackagesError] = useState(false);
    const [deletingDiscount, setDeletingDiscount] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const activeOrganization = useSelector(selectActiveOrganization);
    const systemMoneyFormat = useSelector(selectSystemMoneyFormat);
    const lang = useSelector(selectLang);
    const t = Translate;

    const formatMoney = (value) => formatBillingMoney(value, systemMoneyFormat);

    const loadDiscounts = async () => {
        if (!organizationId) return;

        setLoading(true);
        setLoadError(null);

        try {
            // Katalog faqat nom/narxni ko'rsatish uchun — u yuklanmasa ham chegirmalar ko'rinsin,
            // lekin bu holatni yashirmaymiz: narx ustunlari bo'sh qoladi va chegirma qo'shib bo'lmaydi.
            let catalogFailed = false;
            const [loadedDiscounts, loadedPackages] = await Promise.all([
                billingService.getOrganizationPackageDiscounts({organization_id: organizationId}),
                billingService.getPackages().catch(() => {
                    catalogFailed = true;
                    return [];
                }),
            ]);

            setDiscounts(loadedDiscounts);
            setPackages(loadedPackages);
            setPackagesError(catalogFailed);
        } catch (error) {
            setDiscounts([]);
            setLoadError(getBillingErrorMessage(error) || t(lang, 'common.toast.error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const onAdded = EventBus.on(ADD_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED, () => loadDiscounts());
        const onUpdated = EventBus.on(UPDATE_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED, () => loadDiscounts());
        const onDeleted = EventBus.on(DELETE_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED, () => loadDiscounts());

        return () => {
            EventBus.remove(ADD_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED, onAdded);
            EventBus.remove(UPDATE_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED, onUpdated);
            EventBus.remove(DELETE_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED, onDeleted);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    useEffect(() => {
        loadDiscounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, activeOrganization]);

    const packagesByCode = useMemo(() => {
        const map = {};
        packages.forEach(item => {
            if (item?.code) map[item.code] = item;
        });
        return map;
    }, [packages]);

    // O'chirilgan chegirmalar ro'yxatga tushishi hujjatlashtirilmagan — himoya sifatida filtrlaymiz.
    const activeDiscounts = useMemo(() => discounts.filter(item => !isBillingSoftDeleted(item)), [discounts]);

    const discountedPackageCodes = useMemo(
        () => activeDiscounts.map(item => item?.package?.code).filter(Boolean),
        [activeDiscounts]
    );

    const availablePackages = useMemo(
        () => packages.filter(item => !discountedPackageCodes.includes(item.code)),
        [packages, discountedPackageCodes]
    );

    const onDelete = () => {
        if (!deletingDiscount) return;

        setIsDeleting(true);
        billingService.deleteOrganizationPackageDiscount(deletingDiscount.id)
            .then((deleted) => {
                EventBus.dispatch(DELETE_ORGANIZATION_PACKAGE_DISCOUNT_SUCCEED, deleted);
                toast.success(t(lang, 'common.toast.success'));
                setDeletingDiscount(null);
            })
            .catch((error) => {
                EventBus.dispatch(DELETE_ORGANIZATION_PACKAGE_DISCOUNT_FAILED, error);
                toast.error(getBillingErrorMessage(error) || t(lang, 'common.toast.error'));
            })
            .finally(() => setIsDeleting(false));
    };

    const getPackageName = (discount) => {
        const code = discount?.package?.code;
        return getPackageCodeLabel(code, lang, t, packagesByCode[code]?.name);
    };

    const getPriceAfterDiscount = (discount) => {
        const cost = packagesByCode[discount?.package?.code]?.cost;

        if (cost === null || cost === undefined) return null;

        const price = Number(cost) - Number(discount?.amount || 0);

        return price > 0 ? price : 0;
    };

    return (
        <Fragment>
            <div className={'card-header d-flex flex-wrap gap-2 align-items-center justify-content-between'}>
                <span className={'text-muted fs--1'}>
                    {t(lang, 'admin.organization.package_discounts.description')}
                </span>
                <AddOrganizationPackageDiscountModal organizationId={organizationId}
                                                    packages={availablePackages}
                                                    disabledPackageCodes={discountedPackageCodes}
                />
            </div>

            {!!loadError && <Alert variant={'danger'} className={'mx-3'}>{loadError}</Alert>}

            {!loadError && packagesError && (
                <Alert variant={'warning'} className={'mx-3'}>
                    {t(lang, 'admin.organization.package_discounts.packages_load_error')}
                </Alert>
            )}

            {!loadError && !loading && packages.length > 0 && availablePackages.length === 0 && (
                <Alert variant={'info'} className={'mx-3'}>
                    {t(lang, 'admin.organization.package_discounts.all_packages_have_discount')}
                </Alert>
            )}

            <Table size={'lg'} hover={true} responsive={true}>
                <thead className={'bg-300'}>
                <tr>
                    <th>№</th>
                    <th>{t(lang, 'admin.organization.package_discounts.datatable.column.package')}</th>
                    <th className={'text-end'}>{t(lang, 'admin.organization.package_discounts.datatable.column.package_cost')}</th>
                    <th className={'text-end'}>{t(lang, 'admin.organization.package_discounts.datatable.column.amount')}</th>
                    <th className={'text-end'}>{t(lang, 'admin.organization.package_discounts.datatable.column.price_after_discount')}</th>
                    <th>{t(lang, 'admin.organization.package_discounts.datatable.column.created_at')}</th>
                    <th className={'text-end'}/>
                </tr>
                </thead>
                <tbody>
                {loading && (
                    <tr>
                        <td colSpan={7} className={'text-center py-4'}>
                            <Spinner animation={'border'} size={'sm'} role={'status'}/>
                        </td>
                    </tr>
                )}

                {!loading && activeDiscounts.length === 0 && (
                    <tr>
                        <td colSpan={7} className={'text-center text-muted py-4'}>
                            {t(lang, 'admin.organization.package_discounts.datatable.no_data')}
                        </td>
                    </tr>
                )}

                {!loading && activeDiscounts.map((discount, index) => {
                    const packageCost = packagesByCode[discount?.package?.code]?.cost;
                    const priceAfterDiscount = getPriceAfterDiscount(discount);

                    return (
                        <tr key={discount.id}>
                            <td>{index + 1}</td>
                            <td className={'fw-semi-bold text-black'}>
                                {getPackageName(discount)}
                                <div className={'text-muted fs--2'}>{discount?.package?.code}</div>
                            </td>
                            <td className={'text-end'}>{formatMoney(packageCost)}</td>
                            <td className={'text-end fw-semi-bold text-warning'}>{formatMoney(discount.amount)}</td>
                            <td className={'text-end'}>{priceAfterDiscount === null ? '' : formatMoney(priceAfterDiscount)}</td>
                            <td>{formatBillingDate(discount.created_at)}</td>
                            <td className={'text-end text-nowrap'}>
                                <UpdateOrganizationPackageDiscountModal discount={discount} packages={packages}>
                                    <IconButton className={'mx-1'} size={'sm'} icon={'pencil'} variant={'warning'}/>
                                </UpdateOrganizationPackageDiscountModal>
                                <IconButton className={'mx-1'}
                                            size={'sm'}
                                            icon={'trash'}
                                            variant={'danger'}
                                            onClick={() => setDeletingDiscount(discount)}
                                />
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </Table>

            <Modal show={!!deletingDiscount} onHide={() => setDeletingDiscount(null)} centered={true}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{t(lang, 'admin.organization.package_discounts.delete_modal.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className={'mb-0'}>{t(lang, 'admin.organization.package_discounts.delete_modal.description')}</p>
                    {!!deletingDiscount && (
                        <p className={'mt-2 mb-0 fw-semi-bold text-black'}>
                            {getPackageName(deletingDiscount)} — {formatMoney(deletingDiscount.amount)}
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant={'secondary'} onClick={() => setDeletingDiscount(null)}>
                        {t(lang, 'common.button.cancel')}
                    </Button>
                    <Button variant={'danger'} onClick={onDelete} disabled={isDeleting}>
                        {isDeleting && <Spinner className={'align-middle me-2'} animation={'border'} role={'status'} size={'sm'}/>}
                        {t(lang, 'common.button.delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default OrganizationPackageDiscounts;
